import React, { useState, useEffect } from "react";
import { Box, Button, Collapse, Typography, Checkbox } from "@mui/material";
import {
  Control,
  Controller,
  useFieldArray,
  useFormContext,
} from "react-hook-form";
import FormInput from "@/components/FormInput";
import FormNumberInput from "@/components/FormNumberInput";
import { StyledIconButton } from "@/components/styled";
import SVGSelectArrowDown from "@/assets/icons/SVGSelectArrowDown.tsx";
import SVGSelectArrowUp from "@/assets/icons/SVGSelectArrowUp.tsx";
import FormAsyncSelect from "@/components/FormAsyncSelect";

const BatteryModelsForm = ({ control, disabled = false }: { control: Control; disabled?: boolean }) => {
  const { watch, setValue } = useFormContext();
  const [expandedIndices, setExpandedIndices] = useState<number[]>([]);

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "batteries",
  });

  const toggleExpand = (index: number) => {
    if (disabled) return;
    setExpandedIndices((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  const batteryModelsWatch = watch("batteryModels");
  const batteriesWatch = watch("batteries");
  const manufactoringBrand = watch("manufactoringBrand");

  React.useEffect(() => {
    if (disabled) return;
    
    const currentModels = batteriesWatch?.map(
      (battery: any) => battery.batteryModel
    );

    currentModels?.forEach((model: string, index: number) => {
      if (!batteryModelsWatch?.includes(model)) {
        remove(index);
      }
    });

    batteryModelsWatch?.forEach((model: string) => {
      if (!currentModels.includes(model)) {
        append({
          batteryModel: model,
          batteryTypes: [],
          batteryLocations: "",
          totalCapacityKwh: "",
          totalCapacityIncentive: "",
          nominalPower: "",
        });
        setExpandedIndices((prev) => [...prev, currentModels.length]);
      }
    });
  }, [batteryModelsWatch, batteriesWatch, append, remove, fields.length, disabled]);

  return (
    <Box sx={{}}>
      <FormAsyncSelect
        multiple
        itemsKey={"batteryModels"}
        getItems={async () => {
          if (!manufactoringBrand || manufactoringBrand.trim() === "") {
            return { data: { batteryModels: [] } };
          }

          return { data: { batteryModels: [] } };
        }}
        name={"batteryModels"}
        control={control}
        label={"Battery Models"}
        placeholder={
          !manufactoringBrand || manufactoringBrand.trim() === ""
            ? "Please select a Manufacturing Brand first"
            : "Enter battery model names"
        }
        disabled={disabled || !manufactoringBrand || manufactoringBrand.trim() === ""}
      />

      {fields?.map((item: any, index) => (
        <Box
          sx={{ padding: "8px 0", marginBottom: "16px", marginTop: "-8px" }}
          key={item.id}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
              {item?.batteryModel}
            </Typography>
            {!disabled && (
              <StyledIconButton onClick={() => toggleExpand(index)}>
                {expandedIndices.includes(index) ? (
                  <SVGSelectArrowUp color={"#4E4B48"} />
                ) : (
                  <SVGSelectArrowDown />
                )}
              </StyledIconButton>
            )}
          </Box>
          <Collapse
            sx={{ paddingTop: "16px" }}
            in={expandedIndices.includes(index)}
          >
            <FormInput
              name={`batteries[${index}].batteryTypes`}
              control={control}
              label={"Battery Type"}
              placeholder={"Type name"}
              disabled={disabled}
            />
            <FormInput
              name={`batteries[${index}].inverterModels`}
              control={control}
              label={"Inverter Models"}
              placeholder={"Inverter Models name"}
              disabled={disabled}
            />
            <FormNumberInput
              name={`batteries[${index}].totalCapacityKwh`}
              control={control}
              label={"Nameplate Usable Capacity (kWh)"}
              placeholder={"Battery capacity, kWh"}
              autoFormat={true}
              disabled={disabled}
            />
            <FormNumberInput
              name={`batteries[${index}].totalCapacityIncentive`}
              control={control}
              label={"Capacity Incentive (kWh)"}
              placeholder={"Capacity for incentive calculation, kWh"}
              inputProps={{ min: 0, step: "any" }}
              autoFormat={true}
              disabled={disabled}
            />
            <FormNumberInput
              name={`batteries[${index}].nominalPower`}
              control={control}
              label={"Nominal Power (kW)"}
              placeholder={"Nominal power, kW"}
              autoFormat={true}
              disabled={disabled}
            />
          </Collapse>
        </Box>
      ))}
    </Box>
  );
};

export default BatteryModelsForm;
